import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAllProjectsReview, getProjectTasksReview } from '../../api/reportsApi';
import { usePermissions } from '../PermissionsContext';
import './Reports.css';

const barClass = (consumed) => (consumed > 100 ? 'red' : consumed >= 90 ? 'amber' : 'green');
const fmtH = (v) => (Number(v) || 0).toLocaleString('en-US', { maximumFractionDigits: 1 });
const fmtPct = (v) => `${(Number(v) || 0).toFixed(1)}%`;

const EMPTY_FILTERS = {
  wbs: '', task: '', role: '',
  planMin: '', planMax: '', actualMin: '', actualMax: '', remMin: '', remMax: '',
  consumed: 'All',
};

// Build the real task tree from (ProjectTaskId, ParentTaskId) — NOT by splitting
// WBS strings. Rows are grouped into one node per task (a task may carry several
// per-role rows). A node is a root when it has no parent, or its parent is not
// present among the returned rows (e.g. a demand-less summary task). No "phase"
// nodes are synthesized.
const buildTree = (rows) => {
  const nodeById = new Map();
  rows.forEach((r) => {
    let node = nodeById.get(r.projectTaskId);
    if (!node) {
      node = {
        taskId: r.projectTaskId,
        parentTaskId: r.parentTaskId ?? null,
        outlineLevel: Number(r.outlineLevel) || 0,
        wbs: r.wbs || '',
        taskName: r.taskName,
        rows: [],
        children: [],
      };
      nodeById.set(r.projectTaskId, node);
    }
    node.rows.push(r);
  });

  const roots = [];
  nodeById.forEach((node) => {
    if (node.parentTaskId != null && nodeById.has(node.parentTaskId)) {
      nodeById.get(node.parentTaskId).children.push(node);
    } else {
      roots.push(node);
    }
  });

  const byWbs = (a, b) => String(a.wbs).localeCompare(String(b.wbs), undefined, { numeric: true });
  roots.sort(byWbs);
  nodeById.forEach((n) => n.children.sort(byWbs));
  return { roots, nodeById };
};

export default function ProjectTasksReviewPage() {
  const { hasPermission, loading: permLoading } = usePermissions();
  const allowed = hasPermission?.('Timesheet.ViewProjectReports');

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({}); // taskId -> true
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  useEffect(() => {
    if (!allowed) return;
    (async () => {
      try {
        const dto = await getAllProjectsReview('All', 'ProjectToDate');
        const list = dto?.projects || [];
        setProjects(list);
        if (list.length > 0) setProjectId(String(list[0].projectId));
      } catch (e) {
        const msg = e?.response?.data || e.message;
        setError(typeof msg === 'string' ? msg : 'Failed to load project list.');
      }
    })();
  }, [allowed]);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError('');
    try {
      const dto = await getProjectTasksReview(Number(projectId), null, false);
      setData(dto);
    } catch (e) {
      const msg = e?.response?.data || e.message;
      setError(typeof msg === 'string' ? msg : 'Failed to load project tasks.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (allowed && projectId) load(); }, [allowed, projectId, load]);
  useEffect(() => { setExpanded({}); }, [projectId]); // reset tree when project changes

  const rows = useMemo(() => data?.rows || [], [data]);
  const tree = useMemo(() => buildTree(rows), [rows]);
  const roleOptions = useMemo(
    () => [...new Set(rows.map((r) => r.roleName).filter(Boolean))].sort(),
    [rows]
  );

  const filterActive =
    filters.wbs !== '' || filters.task !== '' || filters.role !== '' ||
    filters.planMin !== '' || filters.planMax !== '' ||
    filters.actualMin !== '' || filters.actualMax !== '' ||
    filters.remMin !== '' || filters.remMax !== '' ||
    filters.consumed !== 'All';

  const inRange = (v, min, max) => {
    const n = Number(v) || 0;
    if (min !== '' && n < Number(min)) return false;
    if (max !== '' && n > Number(max)) return false;
    return true;
  };
  const consumedOk = (c) => {
    switch (filters.consumed) {
      case 'Green': return c < 90;
      case 'Amber': return c >= 90 && c <= 100;
      case 'Red': return c > 100;
      default: return true;
    }
  };
  const passesFilter = useCallback((r) =>
    (filters.wbs === '' || String(r.wbs || '').toLowerCase().includes(filters.wbs.toLowerCase())) &&
    (filters.task === '' || String(r.taskName || '').toLowerCase().includes(filters.task.toLowerCase())) &&
    (filters.role === '' || r.roleName === filters.role) &&
    inRange(r.planHours, filters.planMin, filters.planMax) &&
    inRange(r.actualHours, filters.actualMin, filters.actualMax) &&
    inRange(r.remainingHours, filters.remMin, filters.remMax) &&
    consumedOk(Number(r.consumedPercent) || 0),
    [filters]);

  // When filtering, the visible set is the matching tasks plus every real
  // ParentTaskId ancestor; ancestors are force-expanded so matches show.
  const visibleTaskIds = useMemo(() => {
    if (!filterActive) return null;
    const { nodeById } = tree;
    const set = new Set();
    rows.filter(passesFilter).forEach((r) => {
      set.add(r.projectTaskId);
      let node = nodeById.get(r.projectTaskId);
      while (node && node.parentTaskId != null && nodeById.has(node.parentTaskId)) {
        set.add(node.parentTaskId);
        node = nodeById.get(node.parentTaskId);
      }
    });
    return set;
  }, [filterActive, rows, passesFilter, tree]);

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const clearFilters = () => setFilters(EMPTY_FILTERS);
  const toggleNode = (taskId) => setExpanded((m) => ({ ...m, [taskId]: !m[taskId] }));

  if (!permLoading && !allowed) {
    return <div className="reports-page"><p className="empty-note">You don't have access to project reports.</p></div>;
  }

  const renderLeafRow = (r, outlineLevel) => (
    <tr key={`leaf-${r.projectTaskId}-${r.projectRoleId}`} data-testid={`ptr-leaf-${r.projectTaskId}-${r.projectRoleId}`}>
      <td style={{ paddingLeft: 8 + outlineLevel * 24 + 20 }}>{r.wbs || '—'}</td>
      <td>{r.taskName}</td>
      <td>{r.roleName}</td>
      <td className="num">{fmtH(r.planHours)}</td>
      <td className="num">{fmtH(r.actualHours)}</td>
      <td className="num">{fmtH(r.remainingHours)}</td>
      <td className="num">
        <div className="bar-wrap">
          <div className="bar"><div className={`bar-fill ${barClass(r.consumedPercent)}`} style={{ width: `${Math.min(r.consumedPercent, 100)}%` }} /></div>
          <span className="bar-text">{fmtPct(r.consumedPercent)}</span>
        </div>
      </td>
    </tr>
  );

  const renderTree = () => {
    const out = [];
    const walk = (node) => {
      if (filterActive && !visibleTaskIds.has(node.taskId)) return;
      const isParent = node.children.length > 0;
      const open = filterActive ? true : !!expanded[node.taskId];
      if (isParent) {
        out.push(
          <tr
            key={`node-${node.taskId}`}
            className="ptr-phase-row"
            data-testid={`ptr-node-${node.taskId}`}
            onClick={() => toggleNode(node.taskId)}
            style={{ cursor: 'pointer' }}
          >
            <td style={{ paddingLeft: 8 + node.outlineLevel * 24 }}>
              <span className="ptr-chevron" aria-hidden="true" style={{ marginRight: 6 }}>{open ? '▼' : '▶'}</span>
              {node.wbs || '—'}
            </td>
            <td><strong>{node.taskName}</strong></td>
            <td /><td className="num" /><td className="num" /><td className="num" /><td className="num" />
          </tr>
        );
        if (open) {
          node.rows.forEach((r) => { if (!filterActive || passesFilter(r)) out.push(renderLeafRow(r, node.outlineLevel + 1)); });
          node.children.forEach(walk);
        }
      } else {
        node.rows.forEach((r) => { if (!filterActive || passesFilter(r)) out.push(renderLeafRow(r, node.outlineLevel)); });
      }
    };
    tree.roots.forEach(walk);
    return out;
  };

  return (
    <div className="reports-page" data-testid="project-tasks-review">
      <div className="breadcrumb">Reports · Project Tasks Review</div>
      <h1 className="page-title">Project Tasks Review</h1>
      <p className="page-sub">Per-task planned vs actual hours per role, grouped by the project task hierarchy.</p>

      <div className="filter-bar">
        <label htmlFor="ptr-project">Project</label>
        <select id="ptr-project" value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="ptr-project">
          {projects.map((p) => (
            <option key={p.projectId} value={p.projectId}>{p.projectName}</option>
          ))}
        </select>
      </div>

      {error && <div className="report-error">{error}</div>}

      <div className="panel">
        {loading ? (
          <div className="empty-note">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty-note">No tasks to show for this project.</div>
        ) : (
          <table className="cells ptr-tree-table" data-testid="ptr-table">
            <thead>
              <tr>
                <th>WBS</th><th>Task</th><th>Role</th>
                <th className="num">Plan h</th><th className="num">Actual h</th><th className="num">Remaining</th><th className="num">Consumed</th>
              </tr>
              <tr className="ptr-filter-row">
                <th><input className="ptr-filter-input" value={filters.wbs} onChange={setFilter('wbs')} placeholder="contains" data-testid="ptr-filter-wbs" /></th>
                <th><input className="ptr-filter-input" value={filters.task} onChange={setFilter('task')} placeholder="contains" data-testid="ptr-filter-task" /></th>
                <th>
                  <select className="ptr-filter-input" value={filters.role} onChange={setFilter('role')} data-testid="ptr-filter-role">
                    <option value="">All</option>
                    {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </th>
                <th className="num">
                  <input className="ptr-filter-num" value={filters.planMin} onChange={setFilter('planMin')} placeholder="min" data-testid="ptr-filter-plan-min" />
                  <input className="ptr-filter-num" value={filters.planMax} onChange={setFilter('planMax')} placeholder="max" data-testid="ptr-filter-plan-max" />
                </th>
                <th className="num">
                  <input className="ptr-filter-num" value={filters.actualMin} onChange={setFilter('actualMin')} placeholder="min" data-testid="ptr-filter-actual-min" />
                  <input className="ptr-filter-num" value={filters.actualMax} onChange={setFilter('actualMax')} placeholder="max" data-testid="ptr-filter-actual-max" />
                </th>
                <th className="num">
                  <input className="ptr-filter-num" value={filters.remMin} onChange={setFilter('remMin')} placeholder="min" data-testid="ptr-filter-rem-min" />
                  <input className="ptr-filter-num" value={filters.remMax} onChange={setFilter('remMax')} placeholder="max" data-testid="ptr-filter-rem-max" />
                </th>
                <th className="num">
                  <select className="ptr-filter-input" value={filters.consumed} onChange={setFilter('consumed')} data-testid="ptr-filter-consumed">
                    <option value="All">All</option>
                    <option value="Green">Green (&lt;90)</option>
                    <option value="Amber">Amber (90–100)</option>
                    <option value="Red">Red (&gt;100)</option>
                  </select>
                  <button type="button" className="ptr-clear-btn" onClick={clearFilters} data-testid="ptr-clear-filters">Clear filters</button>
                </th>
              </tr>
            </thead>
            <tbody>
              {renderTree()}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
